import {
  DateField,
  ErrorActionEnum,
  getScaleFromIncrement,
  PresenceEnum,
  type BaseField,
  type FieldData,
  type OrderStrategy,
  type Security,
} from '@talos/kyoko';
import { ParameterKeysEnum } from '../../../tokens/orderStrategy';
import { applyEndTimeDurationToParams } from '../../../utils/order';
import { mapParameterToField } from './Strategies';

type InitializeStrategyParamsResult = { [key: string]: BaseField<FieldData, any> };
// Ideally we will have one initializeStrategyParams for every form but splitting Order form & Sales order form
// for now as both have different validations
//[ UI-4182] - Change Strategy Qty fields validation in order form
export const initializeStrategyParams = ({
  security,
  strategy,
  isModify,
}: {
  security: Security | undefined;
  strategy: OrderStrategy | undefined;
  isModify: boolean;
}): InitializeStrategyParamsResult => {
  const params: InitializeStrategyParamsResult = {};

  if (strategy) {
    strategy.Parameters
      // Only TopLevelHidden param that exists seem to be OrdType and only applicable on Market strategy -- and we can handle that manually
      .filter(param => param.Presence !== PresenceEnum.TopLevelHidden && param.Presence !== PresenceEnum.Hidden)
      .forEach(param => {
        // noImplicitAny 0 - generic value application
        params[param.Name] = mapParameterToField(
          param,
          security?.MinPriceIncrement,
          getScaleFromIncrement(security?.MinSizeIncrement),
          undefined,
          isModify,
          strategy
        );
      });
  }

  return params;
};

/**
 * Builds a parameters object from form fields for SalesOrder requests.
 * Note: Principal orders (OrderService) have different parameter-building logic.
 */
export function buildParametersFromFields(
  fields: Record<string, { value?: string | null }>,
  skipKeys?: string[],
  enableDurationSubmission = false
) {
  const params: Record<string, string | undefined> = { ErrorAction: ErrorActionEnum.Pause };
  const skipSet = new Set(skipKeys);

  for (const [key, field] of Object.entries(fields)) {
    if (skipSet.has(key)) {
      continue;
    }
    // Send explicit undefined for dates, skip other empty fields
    if (field.value == null && !(field instanceof DateField)) {
      continue;
    }
    params[key] = field.value ?? undefined;
  }

  // Send Duration instead of EndTime when Duration was used for EndTime
  const sendDuration = enableDurationSubmission && !(ParameterKeysEnum.Duration in fields);
  if (sendDuration) {
    applyEndTimeDurationToParams(fields[ParameterKeysEnum.EndTime], params);
  }

  return params;
}
